import { XY } from "future-fabric";
import { CameraMemo, CountingLine, FloorPlan, IntervalZone, PseudoCamera } from "~/_legacy/types/analysis";
import {
  BranchVersionResponse,
  CameraMemoResponse,
  CameraMemoTypeResponse,
  CountingLineResponse,
  FloorPlanResponse,
  IntervalZoneResponse,
  PseudoCameraResponse,
  UserResponse,
} from "~/_legacy/types/api";
import { BranchVersion, User } from "~/_legacy/types/common";

export const convertPointsToCvPoints = (points: XY[]) => {
  // [LT, LB, RB, RT] -> [LT, RT, LB, RB].flat()
  const clockWisePoints = [
    points[0], // LT
    points[3], // RT
    points[1], // LB
    points[2], // RB
  ];
  return clockWisePoints.map((p) => [p.x, p.y]).flat();
};

export const convertBranchVersionResponseToBranchVersion = (
  branchVersion: BranchVersionResponse
): BranchVersion => ({
  id: branchVersion.id,
  branchUid: branchVersion.branch_uid,
  name: branchVersion.name,
  description: branchVersion.description,
  createdAt: branchVersion.created_at,
  validFrom: branchVersion.valid_from,
  validTo: branchVersion.valid_until,
  isActive: branchVersion.is_active,
});

export const convertCameraMemoResponseToCameraMemo = (
  cameraMemo: CameraMemoResponse
): CameraMemo => ({
  id: cameraMemo.id,
  type: cameraMemo.type === CameraMemoTypeResponse.Default ? "default" : "adjustmentOrAddition",
  floorPlanId: cameraMemo.floor_plan_id,
  content: cameraMemo.content,
  cameraId: cameraMemo.pseudo_camera_orientation_id,
  author: cameraMemo.user ? convertUserResponseToUser(cameraMemo.user) : null,
  positionForAnalysisSnapshot: cameraMemo.analyzed_coordinates,
  positionForFloorPlan: cameraMemo.floor_plan_coordinates,
  updatedAt: cameraMemo.updated_at,
});

export const convertCountingLineResponseToCountingLine = (
  countingLine: CountingLineResponse
): CountingLine => ({
  id: countingLine.id,
  key: countingLine.key,
  cameraId: countingLine.pseudo_camera_orientation_id,
  analysisConfig: countingLine.analysis_setting_status
    ? {
        name: countingLine.analysis_setting_status.name,
        eventType: countingLine.analysis_setting_status.event_type,
        coordinates: countingLine.analysis_setting_status.coordinates,
      }
    : null,
  floorPlanConfig: countingLine.floor_plan_status ?? null,
});

export const convertFloorPlanResponseToFloorPlan = (floorPlan: FloorPlanResponse): FloorPlan => ({
  id: floorPlan.id,
  branchVersionId: floorPlan.version_id,
  name: floorPlan.name,
  imageUrl: floorPlan.img_url,
  iconSize: floorPlan.icon_size,
  floorNumber: floorPlan.floor_number,
});

export const convertIntervalZoneResponseToIntervalZone = (
  intervalZone: IntervalZoneResponse
): IntervalZone => ({
  id: intervalZone.id,
  key: intervalZone.key,
  cameraId: intervalZone.pseudo_camera_orientation_id,
  analysisConfig: intervalZone.analysis_setting_status
    ? {
        name: intervalZone.analysis_setting_status.name,
        eventType: intervalZone.analysis_setting_status.event_type,
        coordinates: intervalZone.analysis_setting_status.coordinates,
      }
    : null,
  floorPlanConfig: intervalZone.floor_plan_status ?? null,
});

export const convertPseudoCameraResponseToPseudoCamera = (
  pseudoCamera: PseudoCameraResponse
): PseudoCamera => ({
  id: pseudoCamera.id,
  defaultCamId: pseudoCamera.default_cam_id,
  branchUid: pseudoCamera.branch_uid,
});

export const convertUserResponseToUser = (user: UserResponse): User => ({
  name: user.name,
  email: user.email,
  isStaff: user.is_staff,
  client: {
    uid: user.client.uid,
    name: user.client.name,
  },
});
